import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import useBanner from "../../../Services/DomainServices/useBanner";
import { GlobalContext } from "../../../../Context/GlobalContext";

const DRAFT_KEY = "banner-create-draft";
const GENERATED_HTML_KEY = "banner-generated-html";

const EMPTY_FORM = {
  title: "",
  cardType: "",
  status: "N",
  html: "",
};

const getInitialFormData = () => {
  try {
    const savedDraft = sessionStorage.getItem(DRAFT_KEY);
    const generatedHtml = sessionStorage.getItem(GENERATED_HTML_KEY);

    const draft = savedDraft ? JSON.parse(savedDraft) : EMPTY_FORM;

    return {
      ...EMPTY_FORM,
      ...draft,

      html: generatedHtml || draft?.html || "",
    };
  } catch (error) {
    console.log("Banner draft read error:", error);

    return EMPTY_FORM;
  }
};

export default function CreateNewBanner({
  selectedUserId,
  onOpenEditor,
  onCreated,
  onCancel,
}) {
  const navigate = useNavigate();

  const { generatedHTML, setGeneratedHTML } = useContext(GlobalContext);

  const { createBanner } = useBanner();

  const [formData, setFormData] = useState(getInitialFormData);

  const [errors, setErrors] = useState({});

  const [isCreating, setIsCreating] = useState(false);

  /* =========================================================
     SAVE DRAFT
  ========================================================= */

  useEffect(() => {
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(formData));
    } catch (error) {
      console.log("Banner draft save error:", error);
    }
  }, [formData]);

  /* =========================================================
     GET GENERATED HTML AFTER EDITOR
  ========================================================= */

  useEffect(() => {
    const storedGeneratedHTML = sessionStorage.getItem(GENERATED_HTML_KEY);

    const finalHTML = storedGeneratedHTML || generatedHTML;

    if (!finalHTML) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      html: finalHTML,
    }));

    /*
      Generated HTML form me aa gaya,
      temporary key remove kar sakte hain.
    */

    sessionStorage.removeItem(GENERATED_HTML_KEY);

    if (generatedHTML) {
      setGeneratedHTML("");
    }
  }, [generatedHTML, setGeneratedHTML]);

  /* =========================================================
     HANDLE CHANGE
  ========================================================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  /* =========================================================
     OPEN EDITOR
  ========================================================= */

  const handleOpenEditor = () => {
    if (isCreating) return;

    /*
      Editor open hone se pehle
      current form preserve.
    */

    sessionStorage.setItem(DRAFT_KEY, JSON.stringify(formData));

    /*
      Embedded Banner tab flow.
    */

    if (onOpenEditor) {
      onOpenEditor();
      return;
    }

    /*
      Standalone fallback.
    */

    navigate("/inventory/banner/html-editor");
  };

  /* =========================================================
     CREATE BANNER
  ========================================================= */

  const handleSubmit = async () => {

    if (isCreating) {
      return;
    }

    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Banner Title is required.";
    }

    if (!formData.cardType) {
      newErrors.cardType = "Banner Type is required.";
    }

    if (!formData.html.trim()) {
      newErrors.html = "Banner HTML is required.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setIsCreating(true);

    try {
    const success = await createBanner(
      {
        title: formData.title.trim(),

        cardCount: formData.cardType,

        html: formData.html,

        status: formData.status,
      },

      selectedUserId,
    );

      if (!success) {
        return;
      }

      /*
        Create success ke baad
        temporary data clear.
      */

      sessionStorage.removeItem(DRAFT_KEY);

      sessionStorage.removeItem(GENERATED_HTML_KEY);

      setGeneratedHTML("");

      setFormData(EMPTY_FORM);

      /*
        Embedded Banner tab:
        library open karo.
      */

      if (onCreated) {
        onCreated();
        return;
      }

      /*
        Standalone fallback.
      */

      navigate("/inventory/banner");
    } catch (error) {
      console.log("Create Banner Error:", error);
    } finally {
      /*
        IMPORTANT:
        request success/fail dono case me
        button unlock.
      */

      setIsCreating(false);
    }
  };

  /* =========================================================
     CANCEL
  ========================================================= */

  const handleCancel = () => {
    if (isCreating) {
      return;
    }

    setFormData(EMPTY_FORM);
    setErrors({});

    sessionStorage.removeItem(DRAFT_KEY);

    sessionStorage.removeItem(GENERATED_HTML_KEY);

    setGeneratedHTML("");
  };

  return (
    <div className="w-full montserrat-font-1">
      {/* =====================================================
          FORM CARD

          Header / Banner Library / Create Banner tabs
          intentionally removed.

          Banner.jsx already shows them.
      ===================================================== */}

      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "10px",
          border: "1px solid #e5e7eb",
          padding: "24px",
          width: "100%",
          maxWidth: "960px",
        }}
      >
        {/* =================================================
            BANNER TITLE
        ================================================= */}

        <div
          style={{
            marginBottom: "18px",
          }}
        >
          <label style={labelStyle}>Banner Title</label>

          <input
            type="text"
            name="title"
            disabled={isCreating}
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter banner title"
            style={{
              ...inputStyle,

              borderColor: errors.title ? "#ef4444" : "#d1d5db",

              opacity: isCreating ? 0.6 : 1,
            }}
          />

          {errors.title && (
            <p
              style={{
                fontSize: "12px",
                color: "#ef4444",
                marginTop: "4px",
                marginBottom: 0,
              }}
            >
              {errors.title}
            </p>
          )}
        </div>

        {/* =================================================
            BANNER TYPE
        ================================================= */}

        <div
          style={{
            marginBottom: "18px",
          }}
        >
          <label style={labelStyle}>Banner Type</label>

          <div
            style={{
              position: "relative",
            }}
          >
            <select
              name="cardType"
              disabled={isCreating}
              value={formData.cardType}
              onChange={handleChange}
              style={{
                ...inputStyle,

                appearance: "none",

                paddingRight: "36px",

                color: formData.cardType ? "#111827" : "#9ca3af",

                borderColor: errors.cardType ? "#ef4444" : "#d1d5db",

                opacity: isCreating ? 0.6 : 1,
              }}
            >
              <option value="" disabled hidden>
                Select...
              </option>

              <option value="single-carousel">Single Carousel</option>

              <option value="multi-carousel">Multi Carousel</option>
            </select>

            <svg
              style={{
                position: "absolute",

                right: "12px",

                top: "50%",

                transform: "translateY(-50%)",

                pointerEvents: "none",

                color: "#6b7280",
              }}
              width="16"
              height="16"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 11.085l3.71-3.854a.75.75 0 111.08 1.04l-4.25 4.42a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          {errors.cardType && (
            <p
              style={{
                fontSize: "12px",

                color: "#ef4444",

                marginTop: "4px",

                marginBottom: 0,
              }}
            >
              {errors.cardType}
            </p>
          )}
        </div>

        {/* =================================================
            STATUS
        ================================================= */}

        <div
          style={{
            marginBottom: "20px",
          }}
        >
          <label style={labelStyle}>Status</label>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "6px",
            }}
          >
            <div
              onClick={() => {
                if (isCreating) {
                  return;
                }

                setFormData((prev) => ({
                  ...prev,

                  status: prev.status === "Y" ? "N" : "Y",
                }));
              }}
              style={{
                position: "relative",

                width: "40px",

                height: "22px",

                borderRadius: "999px",

                backgroundColor:
                  formData.status === "Y" ? "#22c55e" : "#d1d5db",

                cursor: isCreating ? "not-allowed" : "pointer",

                opacity: isCreating ? 0.6 : 1,

                transition: "background-color 0.25s",

                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: "absolute",

                  top: "3px",

                  left: formData.status === "Y" ? "21px" : "3px",

                  width: "16px",

                  height: "16px",

                  borderRadius: "50%",

                  backgroundColor: "#ffffff",

                  transition: "left 0.25s",

                  boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                }}
              />
            </div>

            <span
              style={{
                fontSize: "13px",

                color: formData.status === "Y" ? "#16a34a" : "#6b7280",

                fontWeight: "500",
              }}
            >
              {formData.status === "Y" ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        {/* =================================================
            BANNER HTML
        ================================================= */}

        <div>
          <label style={labelStyle}>Banner HTML</label>

          <p
            style={{
              fontSize: "12px",

              color: "#9ca3af",

              marginTop: "4px",

              marginBottom: "10px",
            }}
          >
            If you don't have banner HTML, open Editor mode to create your
            banner.
          </p>

          <div
            style={{
              border: `1px solid ${errors.html ? "#ef4444" : "#e5e7eb"}`,

              borderRadius: "8px",

              overflow: "hidden",

              backgroundColor: "#f9fafb",
            }}
          >
            <textarea
              name="html"
              rows={6}
              disabled={isCreating}
              value={formData.html}
              onChange={handleChange}
              placeholder="Place the code here!"
              style={{
                width: "100%",

                padding: "12px",

                border: "none",

                outline: "none",

                resize: "vertical",

                backgroundColor: "transparent",

                fontSize: "13px",

                color: "#3b82f6",

                fontFamily: "monospace",

                boxSizing: "border-box",

                opacity: isCreating ? 0.6 : 1,
              }}
            />

            {/* =============================================
                OPEN EDITOR
            ============================================= */}

            <div
              style={{
                borderTop: "1px solid #e5e7eb",

                padding: "8px 12px",

                backgroundColor: "#f9fafb",
              }}
            >
              <button
                type="button"
                disabled={isCreating}
                onClick={handleOpenEditor}
                style={{
                  display: "inline-flex",

                  alignItems: "center",

                  gap: "6px",

                  fontSize: "13px",

                  color: "#3b82f6",

                  border: "1px solid #e5e7eb",

                  backgroundColor: "#ffffff",

                  padding: "5px 10px",

                  borderRadius: "6px",

                  cursor: isCreating ? "not-allowed" : "pointer",

                  opacity: isCreating ? 0.6 : 1,
                }}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z" />

                  <path d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z" />
                </svg>
                Open Editor mode
              </button>
            </div>
          </div>

          {errors.html && (
            <p
              style={{
                fontSize: "12px",

                color: "#ef4444",

                marginTop: "4px",

                marginBottom: 0,
              }}
            >
              {errors.html}
            </p>
          )}
        </div>

        {/* =================================================
            ACTIONS
        ================================================= */}

        <div
          style={{
            display: "flex",

            gap: "12px",

            marginTop: "20px",
          }}
        >
          {/* CREATE */}

          <button
            type="button"
            disabled={isCreating}
            onClick={handleSubmit}
            style={{
              backgroundColor: "#16a34a",

              color: "#ffffff",

              border: "none",

              padding: "9px 20px",

              borderRadius: "8px",

              fontSize: "14px",

              fontWeight: "500",

              cursor: isCreating ? "not-allowed" : "pointer",

              opacity: isCreating ? 0.6 : 1,
            }}
          >
            {isCreating ? "Creating..." : "Create Banner"}
          </button>

          {/* CANCEL */}

          <button
            type="button"
            disabled={isCreating}
            onClick={handleCancel}
            style={{
              backgroundColor: "#ffffff",

              color: "#374151",

              border: "1px solid #d1d5db",

              padding: "9px 20px",

              borderRadius: "8px",

              fontSize: "14px",

              cursor: isCreating ? "not-allowed" : "pointer",

              opacity: isCreating ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   STYLES
========================================================= */

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "500",
  color: "#374151",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #d1d5db",
  borderRadius: "8px",
  fontSize: "14px",
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
  backgroundColor: "#ffffff",
};
